import { useState, useEffect } from 'react';
import { get, post } from 'aws-amplify/api';
import { UserProfile } from '../types';

interface S3File {
  key: string;
  name: string;
  lastModified: Date;
  size: number;
}

interface FileListResponse {
  files: S3File[];
  folders: string[];
  currentPath: string; // This is the relative path
  fullS3Prefix: string;
  bucketName: string;
}

interface SignedUrlResponse {
  signedUrl: string;
}

export function FileManager({ userProfile }: { userProfile: UserProfile }) {
  const [files, setFiles] = useState<S3File[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [relativePath, setRelativePath] = useState('');
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fullS3Prefix, setFullS3Prefix] = useState('');

  const fetchFiles = async (path = '') => {
    setMessage('Loading files...');
    try {
      const restOperation = get({
        apiName: 'fileApi',
        path: '/files',
        options: { queryParams: { path } },
      });
      const { body } = await restOperation.response;
      const data = (await body.json()) as unknown as FileListResponse;

      setFiles(data.files || []);
      setFolders(data.folders || []);
      setRelativePath(data.currentPath || '');
      setFullS3Prefix(data.fullS3Prefix || '');
      setMessage('');
    } catch (error) {
      console.error('Error fetching files:', error);
      setMessage('Could not load files.');
      setFiles([]);
      setFolders([]);
    }
  };

  useEffect(() => {
    if (userProfile?.company?.baseSftpFolder) {
      fetchFiles();
    }
  }, [userProfile]);

  const navigateTo = (folder: string) => {
    const newPath = relativePath ? `${relativePath}/${folder}` : folder;
    setPathHistory([...pathHistory, relativePath]);
    fetchFiles(newPath);
  };

  const navigateBack = () => {
    setPathHistory((prev) => {
      const newHistory = [...prev];
      const previousPath = newHistory.pop() || '';
      fetchFiles(previousPath);
      return newHistory;
    });
  };

  // New function to go to the root company folder
  const goHome = () => {
    fetchFiles('');
    setPathHistory([]);
  };

  const handleDownload = async (key: string) => {
    try {
      const { body } = await post({
        apiName: 'fileApi',
        path: '/files',
        options: { body: { key, action: 'download' } },
      }).response;
      const { signedUrl } = (await body.json()) as unknown as SignedUrlResponse;
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      } else {
        throw new Error('Signed URL was not returned from the server.');
      }
    } catch (error) {
      console.error('Error getting download link:', error);
      setMessage('Could not get download link.');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userProfile.company?.baseSftpFolder) {
      setMessage('Cannot upload file: company information is missing.');
      return;
    }

    setUploading(true);
    setMessage(`Uploading ${file.name}...`);

    const key = `${fullS3Prefix}/${file.name}`.replace(/\/+/g, '/');

    try {
      const { body } = await post({
        apiName: 'fileApi',
        path: '/files',
        options: { body: { key, action: 'upload' } },
      }).response;
      const { signedUrl } = (await body.json()) as unknown as SignedUrlResponse;

      if (!signedUrl) {
        throw new Error('Could not get a pre-signed URL for upload.');
      }

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      setMessage('Upload successful!');
      fetchFiles(relativePath);
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage('Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // --- BREADCRUMB NAVIGATION COMPONENT ---
  const Breadcrumbs = () => {
    const pathParts = relativePath.split('/').filter((p) => p);
    return (
      <p
        style={{
          fontFamily: 'monospace',
          background: '#f0f0f0',
          padding: '8px',
          borderRadius: '4px',
        }}
      >
        <span style={{ fontWeight: 'bold' }}>Home: </span>
        <span
          onClick={goHome}
          style={{ cursor: 'pointer', color: '#5934C4', fontWeight: 'bold' }}
        >
          {userProfile.company?.baseSftpFolder}
        </span>
        {pathParts.length > 0 && <span> / </span>}
        {pathParts.map((part, index) => {
          const pathToPart = pathParts.slice(0, index + 1).join('/');
          return (
            <span key={index}>
              <span
                onClick={() => fetchFiles(pathToPart)}
                style={{ cursor: 'pointer', color: '#5934C4' }}
              >
                {part}
              </span>
              {index < pathParts.length - 1 && <span> / </span>}
            </span>
          );
        })}
      </p>
    );
  };

  return (
    <div>
      <h4>File Browser</h4>
      <Breadcrumbs />
      {message && (
        <p>
          <em>{message}</em>
        </p>
      )}

      {userProfile.canUpload && (
        <div style={{ margin: '10px 0' }}>
          <label>Upload File: </label>
          <input type='file' onChange={handleUpload} disabled={uploading} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {pathHistory.length > 0 && (
          <span
            onClick={navigateBack}
            style={{
              cursor: 'pointer',
              color: '#5934C4',
              fontWeight: 'bold',
              alignSelf: 'flex-start',
              marginBottom: '10px',
            }}
          >
            &larr; Go Back
          </span>
        )}
        {folders.map((folder) => (
          <div
            key={folder}
            style={{
              padding: '8px',
              borderBottom: '1px solid #eee',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onClick={() => navigateTo(folder)}
          >
            📁 {folder}
          </div>
        ))}
        {files.map((file) => (
          <div
            key={file.key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px',
              borderBottom: '1px solid #eee',
            }}
          >
            <span>
              📄 {file.name} ({formatBytes(file.size)})
            </span>
            <button onClick={() => handleDownload(file.key)}>Download</button>
          </div>
        ))}
      </div>
    </div>
  );
}
